import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/hooks/useApi";
import { logger } from "@/utils/logger";
import { useRef } from "react";

interface RefundRequestData {
  reason: string;
  purchased_at: string;
  expires_at: string;
  duration_days: number;
}

interface RefundResponse {
  success: boolean;
  message: string;
}

export function useBoostRefund() {
  const api = useApi();
  const queryClient = useQueryClient();
  const alreadyRequested = useRef(false);

  const { mutate, isPending, isSuccess, error, reset } = useMutation({
    mutationFn: async (data: RefundRequestData): Promise<RefundResponse> => {
      const { data: result } = await api.post<RefundResponse>(
        "/boost/refund-request",
        data,
      );
      return result;
    },
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["boost-status"] });
      queryClient.invalidateQueries({ queryKey: ["refund-request-status"] });
    },
    onError: (err: Error & { statusCode?: number; status?: number }) => {
      // Check for 409 (already submitted)
      const status = err.statusCode ?? err.status;
      if (status === 409 || err.message?.includes("409")) {
        alreadyRequested.current = true;
        queryClient.invalidateQueries({ queryKey: ["refund-request-status"] });
      }
      logger.warn("Boost refund request failed", err);
    },
  });

  return {
    submitRefund: mutate,
    isPending,
    isSuccess,
    isAlreadyRequested: alreadyRequested.current,
    error: error as Error | null,
    reset: () => {
      alreadyRequested.current = false;
      reset();
    },
  };
}
